<script setup>
import { shallowRef } from "vue";
import GlAlert from "~/components/gitlab/GlAlert.vue";
import GlToggle from "~/components/gitlab/GlToggle.vue";
import EmptyState from "~/components/EmptyState.vue";
import JobCard from "~/components/JobCard.vue";
import { useJobActions } from "~/composables/useJobActions";
import { useProjectActions } from "~/composables/useProjectActions";

const { isAdmin, jobs, projectEnabled, projectId, projectName } = defineProps({
  isAdmin: { default: false, type: Boolean },
  jobs: { required: true, type: Array },
  projectEnabled: { default: true, type: Boolean },
  projectId: { default: null, type: [String, Number] },
  projectName: { default: null, type: String }
});

const localJobs = shallowRef(jobs.map((j) => ({ ...j })));
const localProjectEnabled = shallowRef(projectEnabled);

function updateJob(id, patch) {
  localJobs.value = localJobs.value.map((j) => (j.id === id ? { ...j, ...patch } : j));
}

const { actionAlert, handleToggle, handleExecute } = useJobActions({
  onExecute: (id) => updateJob(id, { lastRunStatus: "running" }),
  onToggle: (id, enabled) => updateJob(id, { enabled })
});

const { actionAlert: projectToggleAlert, handleToggle: handleProjectToggle } = useProjectActions({
  onToggle: (_, enabled) => {
    localProjectEnabled.value = enabled;
  }
});

function onProjectToggle(enabled) {
  handleProjectToggle({ id: projectId }, enabled);
}
</script>

<template>
  <div>
    <!-- Action alert -->
    <GlAlert v-if="actionAlert" :variant="actionAlert.type" class="mb-4" @dismiss="actionAlert = null">
      {{ actionAlert.message }}
    </GlAlert>

    <!-- Project toggle alert -->
    <GlAlert
      v-if="projectToggleAlert"
      :variant="projectToggleAlert.type"
      class="mb-4"
      @dismiss="projectToggleAlert = null"
    >
      {{ projectToggleAlert.message }}
    </GlAlert>

    <div class="rounded-lg border border-[var(--gl-border-color-default)]">
      <div
        class="flex items-center justify-between rounded-t-lg border-b border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-subtle)] px-5 py-3"
      >
        <span class="text-xs font-semibold uppercase tracking-wide text-[var(--gl-text-color-subtle)]">
          Update jobs
        </span>

        <GlToggle
          :model-value="localProjectEnabled"
          :disabled="!isAdmin"
          label="Enabled"
          label-position="left"
          @update:model-value="onProjectToggle($event)"
        />
      </div>

      <!-- Empty state -->
      <EmptyState
        v-if="localJobs.length === 0"
        title="No update jobs found"
        description="No update jobs configured for this project."
      />

      <!-- Jobs list -->
      <ul v-else class="list-none p-0">
        <JobCard
          v-for="job in localJobs"
          :key="job.id"
          :job="job"
          :is-admin="isAdmin"
          :project-id="projectId"
          :project-name="projectName"
          :project-enabled="localProjectEnabled"
          @toggle="handleToggle"
          @execute="handleExecute"
        />
      </ul>
    </div>
  </div>
</template>
